import React, { useState, useMemo } from "react";
import { FlipClassPhaseConfig } from "../../../_services/FlipClassService";
import type { Tag } from "../index";

interface Props {
  config: FlipClassPhaseConfig;
  availableTags: Tag[];
  tagsByType: Record<string, Tag[]>;
  onChange: (
    id: number,
    updates: Partial<{ tags: number[]; autoAssign: boolean; description: string }>
  ) => void;
}

const PHASE_ICONS: Record<string, string> = {
  "pre-class": "📚",
  "in-class": "🏫",
  "post-class": "📝",
};

const PHASE_COLORS: Record<string, string> = {
  "pre-class": "#e0f2fe",
  "in-class": "#dcfce7",
  "post-class": "#fef3c7",
};

const PHASE_BORDER_COLORS: Record<string, string> = {
  "pre-class": "#0284c7",
  "in-class": "#16a34a",
  "post-class": "#ca8a04",
};

const PhaseConfigCard: React.FC<Props> = ({
  config,
  availableTags,
  tagsByType,
  onChange,
}) => {
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState("");

  // Get tag names for selected tags
  const selectedTagObjects = useMemo(() => {
    return config.tags
      .map((tagId) => availableTags.find((t) => t.id === tagId))
      .filter((t): t is Tag => t !== undefined);
  }, [config.tags, availableTags]);

  // Filter tags based on search query
  const filteredTagsByType = useMemo(() => {
    if (!tagSearchQuery.trim()) return tagsByType;

    const query = tagSearchQuery.toLowerCase();
    const filtered: Record<string, Tag[]> = {};

    Object.entries(tagsByType).forEach(([typeName, typeTags]) => {
      const matchingTags = typeTags.filter(
        (tag) =>
          tag.name.toLowerCase().includes(query) ||
          (tag.displayLabel && tag.displayLabel.toLowerCase().includes(query)) ||
          (tag.code && tag.code.toLowerCase().includes(query))
      );
      if (matchingTags.length > 0) {
        filtered[typeName] = matchingTags;
      }
    });

    return filtered;
  }, [tagsByType, tagSearchQuery]);

  // Toggle tag selection
  const handleTagToggle = (tagId: number) => {
    const newTags = config.tags.includes(tagId)
      ? config.tags.filter((id) => id !== tagId)
      : [...config.tags, tagId];
    onChange(config.id, { tags: newTags });
  };

  // Remove a tag
  const handleTagRemove = (tagId: number) => {
    onChange(config.id, { tags: config.tags.filter((id) => id !== tagId) });
  };

  return (
    <div
      className="phase-card"
      style={{
        backgroundColor: PHASE_COLORS[config.phase] || "#f8fafc",
        borderLeftColor: PHASE_BORDER_COLORS[config.phase] || "#64748b",
      }}
    >
      {/* Card Header */}
      <div className="phase-card-header">
        <div className="phase-title">
          <span className="phase-icon">{PHASE_ICONS[config.phase] || "📋"}</span>
          <div>
            <h3>{config.description || config.name}</h3>
            <span className="phase-label">
              {config.name} (Status ID: {config.id})
            </span>
          </div>
        </div>
        <span className="phase-badge">{config.phase}</span>
      </div>

      {/* Tags Field */}
      <div className="form-field">
        <label>Tags</label>
        <div className="tag-selector">
          {/* Selected Tags */}
          <div className="selected-tags">
            {selectedTagObjects.length === 0 ? (
              <span className="placeholder">No tags selected</span>
            ) : (
              selectedTagObjects.map((tag) => (
                <span key={tag.id} className="tag-chip">
                  {tag.displayLabel || tag.name}
                  <button
                    type="button"
                    className="tag-remove"
                    onClick={() => handleTagRemove(tag.id)}
                  >
                    &times;
                  </button>
                </span>
              ))
            )}
            <button
              type="button"
              className="btn-add-tag"
              onClick={() => setIsTagDropdownOpen(!isTagDropdownOpen)}
            >
              + Add Tag
            </button>
          </div>

          {/* Tag Dropdown */}
          {isTagDropdownOpen && (
            <div className="tag-dropdown">
              <div className="tag-search">
                <input
                  type="text"
                  placeholder="Search tags..."
                  value={tagSearchQuery}
                  onChange={(e) => setTagSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="tag-options">
                {Object.entries(filteredTagsByType).length === 0 ? (
                  <div className="no-tags">No matching tags found</div>
                ) : (
                  Object.entries(filteredTagsByType).map(([typeName, typeTags]) => (
                    <div key={typeName} className="tag-group">
                      <div className="tag-group-header">{typeName}</div>
                      {typeTags.map((tag) => (
                        <label key={tag.id} className="tag-option">
                          <input
                            type="checkbox"
                            checked={config.tags.includes(tag.id)}
                            onChange={() => handleTagToggle(tag.id)}
                          />
                          <span className="tag-option-label">
                            {tag.displayLabel || tag.name}
                            {tag.code && (
                              <span className="tag-code">({tag.code})</span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  ))
                )}
              </div>
              <div className="tag-dropdown-footer">
                <button
                  type="button"
                  className="btn-done"
                  onClick={() => {
                    setIsTagDropdownOpen(false);
                    setTagSearchQuery("");
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Auto-Assign Field */}
      <div className="form-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={config.autoAssign}
            onChange={(e) => onChange(config.id, { autoAssign: e.target.checked })}
          />
          <span>Auto-assign resources with these tags</span>
        </label>
        <p className="field-hint">
          When enabled, resources tagged with the above tags will be automatically
          assigned to students when they enter this phase.
        </p>
      </div>

      {/* Description Field */}
      <div className="form-field">
        <label htmlFor={`description-${config.id}`}>Description</label>
        <input
          id={`description-${config.id}`}
          type="text"
          value={config.description}
          onChange={(e) => onChange(config.id, { description: e.target.value })}
          placeholder="Enter a description for this phase..."
        />
      </div>
    </div>
  );
};

export default PhaseConfigCard;
